import { CHART, archetypeColor } from "../../../ForBrands/ui/charts/chartTokens";
import { APPROXIMATE_NOTE, EMPTY, asOfDay, descriptiveLegend, fixed, pct1, pyRound, shortDate, signed1, toNum, topPct } from "../../../../utils/perfFormat";

// Coach Mode · Team > Performance, the game analytics (Jayson's team
// dashboard, BUILD_SPEC B5-B10, B18): the coach/team/performance/ answer
// turned into what each card draws. Pure functions only, tested in
// src/tests/bbperfCoachModel.test.js. Every helper takes the payload as the
// server sends it (already cleaned: no ids, no peer keys, no em dashes) and
// never makes a number up: a missing value is the en dash.

// The objects of a payload list (anything else is skipped).
const objects = (list) => (Array.isArray(list) ? list.filter((x) => x && typeof x === "object") : []);

// ------------------------------------------------------------ the answer
// The tab's state from the hook: "ready" (a payload to draw), "building"
// (the server is still putting it together), "loading" (no answer yet),
// "unavailable" (an answer with a reason) or "idle" (nothing asked).
export const perfStateOf = ({ perf, loading = false, building = false } = {}) => {
  if (perf && perf.available && perf.payload && typeof perf.payload === "object") return "ready";
  if (building) return "building";
  if (loading) return "loading";
  return perf ? "unavailable" : "idle";
};

// Why there are no game analytics, in client words (no data provider).
export const REASON_COPY = {
  not_verified: "Team analytics open once CACHE verifies you as this team's coach.",
  no_feed: "Game analytics need this team's league results. They appear once the team is linked to its league.",
  league_not_supported: "Game analytics are not available for this league yet.",
  no_season: "Game analytics appear once this team has a season on file.",
  no_box_scores: "Game analytics appear once this season's box scores are in.",
  no_record: "No league record matches this team this season yet.",
  feed_error: "Game analytics could not load right now. Try again in a few minutes.",
  building: "This squad's game analytics are still being prepared. Try again in a minute.",
};
// Football and other sports: the section stays out of the way.
const HIDDEN_REASONS = new Set(["not_basketball"]);
// A later request may succeed.
export const RETRY_REASONS = new Set(["feed_error", "building"]);

export const reasonCopy = (reason) => {
  if (HIDDEN_REASONS.has(reason)) return null;
  return REASON_COPY[reason] || REASON_COPY.feed_error;
};

// ------------------------------------------------------------ attribution
// Whether the tab ends with the data attribution line
// (common/PerformanceAttribution, Kobi 10/21): only while the league feed's
// data is on it. Both of the tab's sources are that feed's: the game
// analytics (coach/team/performance/, `state` "ready": built from the league
// feed, a partner team's through its linked league programs) and the team
// style with figures (coach/team/style/, which reads the league feed alone:
// the snapshot's league cells, the archetype radar). The line follows the
// data on screen, not the analytics' reason: a SportsRadar D-I squad
// (league_not_supported) whose season list and style the league feed also
// has shows the feed's figures in the snapshot and the archetype, so it gets
// the line. A team with no league data (a partner team linked to no league,
// a SportsRadar squad the feed does not list, football) has no style figures
// and gets none, and no team gets it while nothing has come yet.
export const teamPerformanceAttributed = ({ state, style, styleLoading = false } = {}) =>
  state === "ready" || (!styleLoading && style?.available === true);

// The day a saved copy was built, in the viewer's own time zone (as
// SavedRosterNotice reads its asOf): "Oct 20"; null without a valid date. A
// bare "2026-10-20" is a calendar day already (no time zone shift). The
// athlete section reads its asOf the same way (perfFormat.asOfDay).
const savedDay = (asOf) => asOfDay(asOf) || null;

// "Showing the numbers saved on Oct 20..." when the server answered from a
// saved copy: the league data did not answer (or is paused), or
// (dataStatus.reason "refreshing") the league answered and the squad's new
// numbers are still being put together, for the next visit.
export const staleNote = (perf) => {
  const status = perf?.dataStatus;
  if (!status || status.state !== "stale") return null;
  const when = savedDay(status.asOf);
  const saved = `Showing the numbers saved${when ? ` on ${when}` : ""}`;
  if (status.reason === "refreshing") return `${saved} while this squad's latest games are added. They update on your next visit.`;
  return `${saved}. They refresh once the league results are reachable again.`;
};

// dataStatus.partial: a read the build tolerates failed this time (the
// roster behind the makeup, or the opponents' games behind strength of
// schedule and RPI), so some figures show a dash. One plain line says so
// (never "No data": the dashes are a failed read, not an empty season).
export const PARTIAL_NOTE = "Some of this squad's numbers could not load this time. They fill in when the numbers next refresh.";
export const partialNote = (perf) => (perf?.dataStatus?.partial ? PARTIAL_NOTE : null);

// Why the makeup cards are missing: its read failed (partial), or the squad
// has no player minutes on file (the package then sends no makeup at all).
export const makeupMissingNote = (perf) =>
  perf?.dataStatus?.partial
    ? "The roster makeup could not load this time."
    : "The roster makeup appears once this squad's player minutes are on file.";

// The squad as the legends name it: the page's display name, else the
// payload's program and division ("Oakland Soldiers 16U").
export const squadLabelOf = (perf) => {
  const display = perf?.team?.displayName;
  if (display) return display;
  const team = perf?.payload?.team || {};
  return [team.program_name, team.division_name].filter(Boolean).join(" ") || "This squad";
};

// "25 with box scores", "1 with a box score".
const withBoxText = (n) => (n === 1 ? "1 with a box score" : `${n} with box scores`);

// What the numbers cover: "Test League 16U · 2025-26 · 26 games, 25 with box scores".
export const coverageLine = (perf) => {
  const payload = perf?.payload || {};
  const team = payload.team || {};
  const log = payload.game_log || {};
  const games = toNum(log.game_count);
  const boxed = toNum(log.games_with_box);
  let gamesText = null;
  if (games !== null) {
    gamesText = `${games} game${games === 1 ? "" : "s"}`;
    if (boxed !== null && boxed < games) gamesText += `, ${withBoxText(boxed)}`;
  }
  const parts = [team.competition || perf?.season?.label, team.season, gamesText].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

// ------------------------------------------------------------ peer group
// The group every "Top N%" on the tab ranks against: the envelope's peer,
// else the payload's own peer block (the same shape); null without one.
export const peerOf = (perf) => {
  const peer = perf?.peer || perf?.payload?.peer;
  return peer && typeof peer === "object" ? peer : null;
};

// "Top N% ranks the squad among 14 Test League 16U squads, 2025-26." (B5:
// the peer n beside the ranks); null when nothing ranks the squad. The
// snapshot opens with it, and the other cards that carry rank chips (the
// shot rates, the volatility) repeat it, as each athlete card names its
// own group.
export const rankLede = (peer) => {
  if (!peer || typeof peer !== "object" || !peer.ranked) return null;
  const n = toNum(peer.n);
  const group = [n !== null ? String(n) : null, peer.label, "squads"].filter(Boolean).join(" ");
  return `Top N% ranks the squad among ${group}${peer.season ? `, ${peer.season}` : ""}.`;
};

// The snapshot's lede: who the "Top N%" chips rank the squad against.
export const peerLede = (peer) => {
  if (!peer || typeof peer !== "object") return null;
  if (!peer.ranked) {
    return peer.building
      ? "Ranks for this league are being built and show on your next visit."
      : "No peer group ranks this league yet, so the tiles show values without ranks.";
  }
  const parts = [rankLede(peer)];
  if (peer.onTheFly) parts.push("The ranks come from a sample of the league's squads.");
  if (peer.approximate) parts.push(APPROXIMATE_NOTE);
  return parts.join(" ");
};

// ------------------------------------------------------------ tiles
// A tile's value as the mock prints it: "%" units with one decimal and a
// percent sign, the net rating signed, everything else one decimal.
export const tileValue = (tile) => {
  const v = toNum(tile?.value);
  if (v === null) return EMPTY;
  if (tile.key === "net") return signed1(v);
  if (tile.unit === "%") return pct1(v);
  return fixed(v, 1);
};

// Metrics that are neither better nor worse (B5): a passive chip that reads
// the plain percentile ("46th pct", RankChip), explained in the card's legend
// (descriptiveNote).
const DESCRIPTIVE = new Set(["pace", "three_par"]);

// The peer group's median as the passive "Med X" chip (B5), in the tile's
// own format ("Med 49.4%", "Med 103.4", "Med +3.7"); null without one.
export const medianLabel = (tile) => {
  const v = toNum(tile?.norm?.median);
  if (v === null) return null;
  return `Med ${tileValue({ ...tile, value: v })}`;
};

// {key, label, name, value, chip: {percentile, kind, approximate, label} | null, median}
// `name` is the metric's own name ("Pace"), for the legend lines; `label`
// the tile's ("Pace · poss/g").
export const tileOf = (tile, overrides = {}) => ({
  key: tile.key,
  label: overrides.label || tile.label || tile.key,
  name: tile.label || overrides.label || tile.key,
  value: tileValue(tile),
  chip: {
    percentile: toNum(tile.percentile),
    kind: overrides.kind || (DESCRIPTIVE.has(tile.key) ? "descriptive" : "rank"),
    approximate: Boolean(tile.norm?.approximate),
    label: null,
  },
  median: medianLabel(tile),
});

const rankedChip = (percentile, label) => ({ percentile: toNum(percentile), kind: "rank", approximate: false, label });

// The legend line of a card whose tiles show a descriptive chip with a
// percentile (B5): "Pace: percentile of how much, not how well", with the
// metrics' own names, or `name` for a card of such figures ("Roster
// figures"); null when none shows one.
export const descriptiveNote = (tiles, name) => {
  const shown = objects(tiles).filter((t) => t.chip && t.chip.kind === "descriptive" && toNum(t.chip.percentile) !== null);
  if (!shown.length) return null;
  return descriptiveLegend(name ? [name] : shown.map((t) => t.name || t.label));
};

// Net & context (B7): Net, Pace, Record, SOS and RPI with the mock's labels.
export const contextTile = (tile) => {
  switch (tile.key) {
    case "net":
      return tileOf(tile, { label: "Net Rtg" });
    case "pace":
      return tileOf(tile, { label: "Pace · poss/g", kind: "descriptive" });
    case "record": {
      const win = toNum(tile.win_pct);
      return {
        key: "record",
        label: "Record",
        value: tile.value ? String(tile.value) : EMPTY,
        chip: win === null ? null : rankedChip(win, `${fixed(win, 1)}% wins`),
        median: null,
      };
    }
    case "sos": {
      const rank = toNum(tile.rank);
      const of = toNum(tile.of);
      return {
        key: "sos",
        label: "SOS · opp win %",
        value: pct1(tile.value),
        chip: rankedChip(tile.percentile, rank !== null && of !== null ? `#${rank} of ${of}` : null),
        median: null,
      };
    }
    case "rpi": {
      const rank = toNum(tile.rank);
      const of = toNum(tile.of);
      const v = toNum(tile.value);
      return {
        key: "rpi",
        label: v === null ? "RPI rank" : `RPI rank · ${v.toFixed(3)}`,
        value: rank === null ? EMPTY : `#${rank}`,
        chip: rankedChip(rank === null ? null : tile.percentile, rank !== null && of !== null ? `of ${of} squads` : null),
        median: null,
      };
    }
    default:
      return tileOf(tile);
  }
};

// The snapshot's three groups (B7): Offense 5 tiles, Defense 4, Net &
// context 5. The group dots are category marks (square), not bands.
export const SNAPSHOT_GROUPS = [
  { key: "offense", label: "Offense", color: CHART.orange },
  { key: "defense", label: "Defense", color: CHART.info },
  { key: "context", label: "Net & context", color: CHART.ink },
];
// How many tiles each group holds while it loads.
export const SNAPSHOT_SHAPE = { offense: 5, defense: 4, context: 5 };

export const snapshotGroups = (payload) => {
  const snap = payload?.snapshot || {};
  return SNAPSHOT_GROUPS.map((group) => ({
    ...group,
    tiles: objects(snap[group.key]).map((t) => (group.key === "context" ? contextTile(t) : tileOf(t))),
  })).filter((group) => group.tiles.length);
};

// Every snapshot tile by key (the four factors' peer medians read them).
export const snapshotTilesByKey = (payload) => {
  const snap = payload?.snapshot || {};
  return Object.fromEntries(
    [...objects(snap.offense), ...objects(snap.defense), ...objects(snap.context)].filter((t) => t.key).map((t) => [t.key, t]),
  );
};

// The band legend under the snapshot (B5): the dots on the rank chips.
export const BAND_LEGEND = [
  { key: "positive", swatch: "dot", color: CHART.positive, label: "Top 40%" },
  { key: "attention", swatch: "dot", color: CHART.attention, label: "Middle" },
  { key: "critical", swatch: "dot", color: CHART.critical, label: "Bottom 35%" },
];

// A card's reads when the payload has none (the mock's empty callout).
export const NO_PATTERN = "No notable pattern for this squad.";

// ------------------------------------------------------------ four factors
// The track each factor's bars fill: the mock's scale, grown to 1.15 x the
// largest value or median on the row when one passes it, so no bar and no
// median tick is pinned to the end of the track (notes 10/21 D5); a factor
// the mock has no scale for uses 1.4 x the largest value on the row.
const FACTOR_SCALE = { efg: 70, tov: 30, reb: 50, ftr: 60 };
const FACTOR_HEADROOM = 1.15;

const factorScale = (factor, largest) => {
  const base = Object.prototype.hasOwnProperty.call(FACTOR_SCALE, factor) ? FACTOR_SCALE[factor] : null;
  return base ? Math.max(base, Math.ceil(largest * FACTOR_HEADROOM)) : Math.max(10, Math.ceil(largest * 1.4));
};

export const fourFactorRows = (payload) => {
  const tiles = snapshotTilesByKey(payload);
  return objects(payload?.four_factors?.factors).map((f, i) => {
    const team = toNum(f.offense?.value);
    const opponents = toNum(f.defense?.value);
    const median = toNum(tiles[f.offense?.metric]?.norm?.median);
    const largest = Math.max(team || 0, opponents || 0, median || 0);
    return {
      key: f.factor || `factor-${i}`,
      label: f.label || f.factor || "",
      edge: f.edge === true ? "edge" : f.edge === false ? "deficit" : null,
      team,
      opponents,
      opponentsLabel: f.defense?.label || "Opponents",
      median,
      scale: factorScale(f.factor, largest),
    };
  });
};

// ------------------------------------------------------------ games
// "70–79" (en dash), the squad's score first.
export const scoreOf = (game) => {
  const a = toNum(game?.team_score);
  const b = toNum(game?.opponent_score);
  if (a === null && b === null) return EMPTY;
  return `${a === null ? EMPTY : pyRound(a)}–${b === null ? EMPTY : pyRound(b)}`;
};

// "+35", "−9" (a true minus), "0".
export const signedWhole = (v) => {
  const n = toNum(v);
  if (n === null) return EMPTY;
  const r = pyRound(n);
  if (r === 0) return "0";
  return `${r > 0 ? "+" : "−"}${Math.abs(r)}`;
};

const RESULT_WORDS = { W: "Win", L: "Loss", T: "Tie" };
export const resultWord = (result) => RESULT_WORDS[result] || null;

// "Win by 35", "Loss by 9", "Tie"; null without a result.
export const resultLine = (result, margin) => {
  const word = resultWord(result);
  if (!word) return null;
  const m = toNum(margin);
  return m === null || m === 0 ? word : `${word} by ${Math.abs(pyRound(m))}`;
};

// The game's event in short: "2026 League - Session II (Memphis)" -> "Session II (Memphis)".
export const eventOf = (names) => {
  const first = Array.isArray(names) ? names.find(Boolean) : names;
  if (!first) return null;
  const text = String(first);
  const tail = text.includes(" - ") ? text.split(" - ").pop() : text.replace(/^\d{4}\s+/, "");
  return tail.trim() || null;
};

// The game log's numeric columns (T3). `shade` cells are tinted against
// the squad's own season average, `invert` where lower is better.
export const GAME_LOG_STATS = [
  { key: "pace", label: "Pace", title: "Possessions per game", digits: 0 },
  { key: "ortg", label: "ORtg", title: "Points scored per 100 possessions", digits: 0, shade: true },
  { key: "drtg", label: "DRtg", title: "Points allowed per 100 possessions", digits: 0, shade: true, invert: true },
  { key: "net", label: "Net", title: "Net rating: ORtg minus DRtg", signed: true },
  { key: "efg_pct", label: "eFG%", title: "Effective field goal percentage", digits: 1, shade: true },
  { key: "opp_efg_pct", label: "Opp eFG%", title: "Opponents' effective field goal percentage", digits: 1, shade: true, invert: true, opponent: true },
  { key: "three_pct", label: "3P%", title: "Three-point percentage", digits: 1, shade: true },
  { key: "tov_pct", label: "TOV%", title: "Turnovers per 100 possessions", digits: 1, shade: true, invert: true },
  { key: "oreb_pct", label: "OR%", title: "Offensive rebound percentage", digits: 1 },
  { key: "ft_rate", label: "FTr", title: "Free throw rate", digits: 1 },
];

const statOf = (game, column) => {
  if (!game?.has_box) return null;
  return toNum(column.opponent ? game.opponent_stats?.[column.key.replace(/^opp_/, "")] : game.stats?.[column.key]);
};

// Mean and population standard deviation (null for fewer than two values
// or no spread).
export const spreadOf = (values) => {
  const list = values.filter((v) => v !== null);
  if (list.length < 2) return null;
  const mean = list.reduce((a, b) => a + b, 0) / list.length;
  const sd = Math.sqrt(list.reduce((a, b) => a + (b - mean) ** 2, 0) / list.length);
  return sd > 0 ? { mean, sd } : null;
};

// The heat tint of a cell (the mock's shade): z against the squad's own
// season, clipped at ±2, alpha |z| / 2 x 0.34; better is bm-positive, worse
// bm-critical. Null for no value or no spread.
export const shadeOf = (value, spread, invert = false) => {
  const v = toNum(value);
  if (v === null || !spread) return null;
  let z = (v - spread.mean) / spread.sd;
  if (invert) z = -z;
  z = Math.max(-2, Math.min(2, z));
  const alpha = Math.round((Math.abs(z) / 2) * 0.34 * 100) / 100;
  if (alpha <= 0) return null;
  return { better: z >= 0, alpha };
};

// "rgba(31, 157, 85, 0.17)" from a bm hex and an alpha.
export const rgbaOf = (hex, alpha) => {
  const h = String(hex).replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const shadeColor = (shade) => (shade ? rgbaOf(shade.better ? CHART.positive : CHART.critical, shade.alpha) : null);

export const gameLogView = (payload) => {
  const log = payload?.game_log || {};
  const games = objects(log.games);
  const boxed = games.filter((g) => g.has_box);
  const spreads = Object.fromEntries(
    GAME_LOG_STATS.filter((c) => c.shade).map((c) => [c.key, spreadOf(boxed.map((g) => statOf(g, c)))]),
  );
  const rows = games.map((g, i) => {
    const cells = Object.fromEntries(
      GAME_LOG_STATS.map((c) => {
        const value = statOf(g, c);
        return [
          c.key,
          {
            value,
            text: c.signed ? signed1(value) : fixed(value, c.digits),
            shade: c.shade ? shadeOf(value, spreads[c.key], c.invert) : null,
          },
        ];
      }),
    );
    return {
      key: `game-${g.order ?? i}`,
      date: shortDate(g.date),
      opponent: g.opponent || EMPTY,
      event: eventOf(g.event_names),
      loc: g.loc || EMPTY,
      result: g.result || null,
      score: scoreOf(g),
      margin: toNum(g.margin),
      marginText: signedWhole(g.margin),
      hasBox: Boolean(g.has_box),
      cells,
    };
  });
  const gameCount = toNum(log.game_count) ?? games.length;
  const withBox = toNum(log.games_with_box) ?? boxed.length;
  const showLoc = games.some((g) => g.loc);
  const count = `${gameCount} game${gameCount === 1 ? "" : "s"}, ${withBoxText(withBox)}.`;
  const notes = [count];
  if (boxed.length > 1) notes.push("Shading compares each game with the squad's own season average.");
  if (showLoc && games.some((g) => !g.loc)) notes.push("A dash under Loc means no venue on file.");
  // `note` describes the desktop table; `listNote` the phone list, which has
  // no shading and no Loc column (a game's venue shows only when known).
  return {
    rows,
    showLoc,
    gameCount,
    withBox,
    note: notes.join(" "),
    listNote: count,
    shaded: rows.some((r) => Object.values(r.cells).some((c) => c.shade)),
  };
};

// ------------------------------------------------------------ form (T5)
export const formView = (payload) => {
  const log = payload?.game_log || {};
  const byOrder = new Map(objects(log.games).map((g) => [g.order, g]));
  const rows = objects(log.rolling_net).filter((r) => toNum(r.rolling_net) !== null || toNum(r.net) !== null);
  const rollingWindow = toNum(log.rolling_window);
  const points = rows.map((r, i) => {
    const game = byOrder.get(r.order) || null;
    const date = shortDate(r.date);
    const opponent = game?.opponent || null;
    return {
      key: `form-${r.order ?? i}`,
      label: date,
      value: toNum(r.net),
      rolling: toNum(r.rolling_net),
      opponent,
      result: game?.result || null,
      score: game ? scoreOf(game) : EMPTY,
      ariaLabel: `${date}${opponent ? ` vs ${opponent}` : ""}: Net ${signed1(r.net)}, rolling ${signed1(r.rolling_net)}`,
    };
  });
  const last = [...points].reverse().find((p) => p.rolling !== null);
  return { points, current: last ? last.rolling : null, season: toNum(log.season?.net), window: rollingWindow };
};

export const formTitle = (rollingWindow) =>
  toNum(rollingWindow) ? `Form: Net Rating (${toNum(rollingWindow)}-Game Rolling)` : "Form: Net Rating (Rolling)";

// ------------------------------------------------------------ consistency (T7)
// The swings need two games with a box score (one game has no spread: the
// package leaves σ empty), so a shorter series draws no chart, only a note.
export const MIN_SWING_GAMES = 2;

export const consistencyView = (payload) => {
  const c = payload?.consistency || {};
  const items = objects(c.series).map((s, i) => {
    const date = shortDate(s.date);
    // In words, as the Form points read: "Net −11.4, loss by 9".
    const line = resultLine(s.result, s.margin);
    return {
      key: `net-${s.order ?? i}`,
      value: toNum(s.net),
      date,
      opponent: s.opponent || null,
      result: s.result || null,
      margin: toNum(s.margin),
      ariaLabel: `${date}${s.opponent ? ` vs ${s.opponent}` : ""}: Net ${signed1(s.net)}${line ? `, ${line.toLowerCase()}` : ""}`,
    };
  });
  const sigma = c.net_sd && typeof c.net_sd === "object" ? c.net_sd : null;
  const blowouts = c.blowouts && typeof c.blowouts === "object" ? c.blowouts : null;
  const threshold = toNum(blowouts?.threshold);
  let chartNote = null;
  if (items.length < MIN_SWING_GAMES) {
    chartNote = items.length
      ? "Game-to-game swings appear once two games have box scores."
      : "Game-to-game swings appear once games have box scores.";
  }
  return {
    items,
    chartNote,
    sigma: sigma
      ? {
          value: fixed(sigma.value, 1),
          chip: { percentile: toNum(sigma.percentile), kind: "rank", approximate: Boolean(sigma.norm?.approximate), label: null },
          median: toNum(sigma.norm?.median) === null ? null : `Med ${fixed(sigma.norm.median, 1)}`,
        }
      : { value: EMPTY, chip: null, median: null },
    // The volatility carries a "Top N%" (the card names its peer group).
    ranked: toNum(sigma?.percentile) !== null,
    blowoutLabel: threshold !== null ? `Blowout W / L (±${threshold})` : "Blowout W / L",
    blowouts: blowouts ? `${toNum(blowouts.wins) ?? EMPTY} / ${toNum(blowouts.losses) ?? EMPTY}` : EMPTY,
    insights: Array.isArray(c.insights) ? c.insights : [],
  };
};

// ------------------------------------------------------------ shot profile (T6)
const shareWhole = (share) => {
  const v = toNum(share);
  return v === null ? EMPTY : `${pyRound(v)}%`;
};

export const shotProfileView = (payload) => {
  const sp = payload?.shot_profile || {};
  const segments = objects(sp.points_mix).map((m) => ({ key: m.key, label: m.label, share: toNum(m.share) }));
  const opp = sp.opponent && typeof sp.opponent === "object" ? sp.opponent : null;
  const oppMix = opp ? objects(opp.points_mix).map((m) => `${shareWhole(m.share)} ${m.label}`) : [];
  const oppRates = opp ? [`3PA rate ${pct1(opp.three_par)}`, `3P% ${pct1(opp.three_pct)}`].join(", ") : null;
  const rates = objects(sp.rates).map((r) => tileOf(r));
  return {
    segments,
    opponentLine: opp ? `Opponents: ${[...oppMix, oppRates].filter(Boolean).join(" · ")}` : null,
    rates,
    // Some rate carries a "Top N%" (3PA rate is descriptive: passive).
    ranked: rates.some((t) => t.chip.kind === "rank" && t.chip.percentile !== null),
    descriptiveNote: descriptiveNote(rates),
    insights: Array.isArray(sp.insights) ? sp.insights : [],
  };
};

// ------------------------------------------------------------ makeup (T8-T12)
// The makeup block, or null when the roster call failed or it is missing.
export const makeupOf = (payload) => {
  const mk = payload?.makeup;
  if (!mk || typeof mk !== "object" || mk.available === false || mk.error) return null;
  return mk;
};

// The kind overline of each makeup insight card (T8).
export const MAKEUP_KIND_LABELS = {
  missing_position: "Missing position",
  thin_position: "Position gap",
  heavy_position: "Position mix",
  missing_role: "Role gap",
  avg_height: "Size",
  rotation_size: "Rotation",
  top_scorer_share: "Scoring",
  performance_link: "Linked to performance",
  roster: "Roster",
};
export const NO_GAPS = { kind: "roster", severity: "info", title: "No gaps against peers", detail: "Position and role mix sit within peer norms." };

// 76 -> 6'4"
export const feetInches = (inches) => {
  const v = toNum(inches);
  if (v === null) return null;
  const whole = pyRound(v);
  return `${Math.floor(whole / 12)}'${whole % 12}"`;
};

// A position cutoff (G if height < g_f, C if height >= f_c; learned ones
// are fractional, 76.25) as the first whole inch on its far side, so the
// caption is exact for listed heights: 76.25 reads 6'5" (a 6'4" player is a
// guard), 76.0 reads 6'4" (a 6'4" player is a forward).
export const cutoffHeight = (inches) => {
  const v = toNum(inches);
  return v === null ? null : feetInches(Math.ceil(v - 1e-9));
};

export const makeupCaption = (mk, peerLabel) => {
  const cov = mk?.coverage || {};
  const peer = mk?.peer || {};
  const rotation = toNum(cov.rotation);
  const squads = toNum(peer.n_squads);
  const first =
    peer.available && squads !== null
      ? `${rotation ?? EMPTY} rotation players compared with ${squads} ${peerLabel ? `${peerLabel} ` : ""}squads.`
      : `${rotation ?? EMPTY} rotation players. No peer group to compare with yet.`;
  const listed = toNum(cov.position_listed);
  const inferred = toNum(cov.position_inferred);
  let positions = null;
  if (listed !== null || inferred !== null) {
    positions = `Positions: ${listed ?? 0} listed, ${inferred ?? 0} inferred from height`;
    const gf = cutoffHeight(peer.position_cutoffs?.g_f);
    const fc = cutoffHeight(peer.position_cutoffs?.f_c);
    positions += inferred && gf && fc ? ` (guards under ${gf}, centers from ${fc}).` : ".";
  }
  return [first, positions].filter(Boolean).join(" ");
};

// The four makeup metrics (descriptive: a passive "Nth pct" chip, B5; the
// card's legend says what it means, MAKEUP_FIGURES).
// "8", "8.5": one decimal unless it is a whole number.
const trimmed = (v) => fixed(v, 1).replace(/\.0$/, "");

// A makeup metric's peer median in its display's format: height in feet
// and inches, shares as whole percents ("Med 6'5\"", "Med 66%", "Med 8").
export const makeupMedianLabel = (metric) => {
  const v = toNum(metric?.peers_median);
  if (v === null) return null;
  if (metric.unit === "in") return `Med ${feetInches(v)}`;
  if (metric.unit === "%") return `Med ${pyRound(v)}%`;
  return `Med ${trimmed(v)}`;
};

export const MAKEUP_FIGURES = "Roster figures";

export const makeupTiles = (mk) =>
  objects(mk?.metrics).map((m) => ({
    key: m.key,
    label: m.label || m.key,
    name: m.label || m.key,
    value: m.display ? String(m.display) : fixed(m.value, 1),
    chip: { percentile: toNum(m.percentile), kind: "descriptive", approximate: false, label: null },
    median: makeupMedianLabel(m),
  }));

// Roster Mix vs Peers (T9): one row per archetype in payload order, the
// track scaled to max(40, the largest share).
export const compositionView = (mk) => {
  const rows = objects(mk?.composition).map((c, i) => ({
    key: `${c.archetype || "archetype"}-${i}`,
    archetype: c.archetype || EMPTY,
    color: archetypeColor(c.archetype, c.color),
    team: toNum(c.team),
    peers: toNum(c.peers),
    diff: toNum(c.diff),
  }));
  const scale = Math.max(40, ...rows.flatMap((r) => [r.team || 0, r.peers || 0]));
  return { rows, scale };
};

// Position Depth (T10): G / F / C, a column flagged when an insight calls
// the position missing or thin (not heavy).
const GAP_KINDS = { missing_position: "missing", thin_position: "thin" };
export const positionView = (mk) => {
  const gaps = {};
  objects(mk?.insights).forEach((i) => {
    if (GAP_KINDS[i.kind] && i.position && !gaps[i.position]) gaps[i.position] = GAP_KINDS[i.kind];
  });
  const columns = objects(mk?.positions).map((p) => {
    const count = toNum(p.count);
    const peersCount = toNum(p.peers_count);
    const gap = gaps[p.position] || null;
    const footer = [`${count ?? EMPTY} in rotation`, `peers ${peersCount === null ? EMPTY : peersCount.toFixed(1)}`, gap].filter(Boolean).join(" · ");
    return { key: p.position || p.label, label: p.label || p.position, code: p.position || null, team: toNum(p.team), peers: toNum(p.peers), footer, alert: Boolean(gap) };
  });
  const scale = Math.max(60, ...columns.flatMap((c) => [c.team || 0, c.peers || 0]));
  const covered = toNum(mk?.coverage?.minutes_with_position);
  return { columns, scale, caption: covered === null ? null : `${fixed(covered, 1)}% of minutes have a listed or inferred position.` };
};

// Rotation (T11): every rotation player plus anyone else in the top 12 by
// minutes (the payload is sorted by minutes); fill-ins are greyed.
export const ROTATION_SHOWN = 12;
export const MINUTES_SHARE_FULL = 20; // a 20% share fills the mini bar

const gradClassOf = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY;
  return String(value).replace(/\.0$/, "");
};

export const rotationView = (mk) => {
  const roster = objects(mk?.roster);
  const shown = roster.filter((r, i) => r.rotation || i < ROTATION_SHOWN);
  const rows = shown.map((r, i) => {
    const share = toNum(r.minutes_share);
    const inferred = r.position_source === "height";
    return {
      key: `${r.player_name || "player"}-${i}`,
      name: r.player_name || "Unnamed player",
      archetype: r.archetype || null,
      color: r.archetype ? archetypeColor(r.archetype, r.color) : null,
      position: r.position ? `${r.position}${inferred ? "*" : ""}` : EMPTY,
      inferred: Boolean(r.position && inferred),
      height: r.height || EMPTY,
      gradClass: gradClassOf(r.grad_class),
      gp: toNum(r.games_played) === null ? EMPTY : String(pyRound(r.games_played)),
      mpg: fixed(r.minutes_per_game, 1),
      ppg: fixed(r.points_per_game, 1),
      rpg: fixed(r.rebounds_per_game, 1),
      apg: fixed(r.assists_per_game, 1),
      share: pct1(share),
      shareFill: share === null ? 0 : Math.max(0, Math.min(100, (share / MINUTES_SHARE_FULL) * 100)),
      rotation: Boolean(r.rotation),
      profile: typeof r.profile === "string" && r.profile ? r.profile : null,
    };
  });
  const hidden = roster.length - shown.length;
  const head = rows.some((r) => r.inferred) ? ["* Position inferred from height."] : [];
  const tail = [];
  if (hidden > 0) tail.push(`${hidden} more fill-in${hidden === 1 ? "" : "s"} not shown.`);
  tail.push("Archetypes rate each player against the division's rotation players.");
  const grey = rows.some((r) => !r.rotation) ? ["Grey rows are fill-ins outside the rotation."] : [];
  // `note` is the desktop table's; `listNote` the phone cards', which have
  // no grey rows (each fill-in card says "Fill-in").
  return { rows, note: [...head, ...grey, ...tail].join(" "), listNote: [...head, ...tail].join(" ") };
};

// "TP" from "Test Player 0401": the first letters of the first two words
// that start with a letter.
export const initialsOfName = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const meanOf = (values) => {
  const list = values.map(toNum).filter((v) => v !== null);
  return list.length ? { mean: list.reduce((a, b) => a + b, 0) / list.length, count: list.length } : null;
};

// Player spotlight (T12) from the rotation: most minutes (the first row),
// the leading scorer (highest PPG among the rows that have one) and the
// highest-rated (the best mean of the five archetype percentiles). A pick
// with no data keeps its tile with a plain note. Null without a rotation.
export const spotlightOf = (mk) => {
  const rot = objects(mk?.roster).filter((r) => r.rotation);
  if (!rot.length) return null;
  const most = rot[0];
  // The leading scorer among the rows with a scoring average; without one
  // the tile waits (never a name picked by default).
  const scorers = rot.filter((r) => toNum(r.points_per_game) !== null);
  const scorer = scorers.reduce((best, r) => (!best || toNum(r.points_per_game) > toNum(best.points_per_game) ? r : best), null);
  const rated = rot
    .map((r) => ({ r, avg: meanOf(Object.values(r.pct && typeof r.pct === "object" ? r.pct : {})) }))
    .filter((x) => x.avg)
    .reduce((best, x) => (!best || x.avg.mean > best.avg.mean ? x : best), null);
  const entry = (key, eyebrow, r, tag, emptyNote) => ({
    key,
    eyebrow,
    name: r ? r.player_name || "Unnamed player" : null,
    initials: r ? initialsOfName(r.player_name) : null,
    tag: r ? tag || null : null,
    profile: r && typeof r.profile === "string" ? r.profile : null,
    emptyNote: r ? null : emptyNote,
  });
  // "28.8 mpg", "15.0% of minutes": a figure the row lacks is left out
  // (never "– mpg" or "–% of minutes").
  const figure = (v, text) => (toNum(v) === null ? null : text);
  const tagOf = (...parts) => parts.filter(Boolean).join(" · ");
  return [
    entry(
      "minutes",
      "Most minutes",
      most,
      tagOf(figure(most.minutes_per_game, `${fixed(most.minutes_per_game, 1)} mpg`), figure(most.minutes_share, `${pct1(most.minutes_share)} of minutes`)),
    ),
    entry(
      "scorer",
      "Leading scorer",
      scorer,
      scorer ? tagOf(`${fixed(scorer.points_per_game, 1)} ppg`, scorer.archetype) : null,
      "Appears once the rotation's scoring is on file",
    ),
    entry(
      "rated",
      "Highest-rated",
      rated?.r,
      rated ? `${topPct(rated.avg.mean)} on average across ${rated.avg.count} archetype metric${rated.avg.count === 1 ? "" : "s"}` : null,
      "Appears once rotation players carry archetype ratings",
    ),
  ];
};

// ------------------------------------------------------------ the page
// The collapsible cards on the tab, in page order (the Collapse all
// control drives these ids).
export const PERF_CARD_IDS = {
  snapshot: "team-perf-snapshot",
  archetype: "team-perf-archetype",
  gameLog: "team-perf-game-log",
  fourFactors: "team-perf-four-factors",
  form: "team-perf-form",
  shot: "team-perf-shot-profile",
  consistency: "team-perf-consistency",
  gaps: "team-perf-roster-gaps",
  mix: "team-perf-roster-mix",
  depth: "team-perf-position-depth",
  rotation: "team-perf-rotation",
};

export const perfCardIds = (state, payload) => {
  const ids = [PERF_CARD_IDS.snapshot, PERF_CARD_IDS.archetype];
  if (state !== "ready") return ids;
  ids.push(PERF_CARD_IDS.gameLog, PERF_CARD_IDS.fourFactors, PERF_CARD_IDS.form, PERF_CARD_IDS.shot, PERF_CARD_IDS.consistency);
  if (makeupOf(payload)) ids.push(PERF_CARD_IDS.gaps, PERF_CARD_IDS.mix, PERF_CARD_IDS.depth, PERF_CARD_IDS.rotation);
  return ids;
};
